import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import {
    OdataValueHelperInstanceToken,
    OdataValueHelperInterface,
} from '../../interfaces/odata-value-helper.interface';
import { ValueHelperInterface } from '../../interfaces/value-helper.interface';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../models/complex/time-recording-entry.model';
import { StatusModel } from '../../models/low-level/status.model';
import { ValueHelperModel } from '../../models/low-level/value-helper.model';
import { TextModel } from '../../models/primitive/text.model';
import { FormParticipantTitles } from '../../types/form-participant-titles';
import { Mutable } from '../../types/mutable';
import { EventService } from '../event/event.service';
import { PaceInsightsService } from '@evorait/pace-logging';

@Injectable()
export class ValueHelperService implements InitializableInterface, ValueHelperInterface {
    public isInitialized = false;
    public codeListForId: Record<string, string[]> = {};

    private models: ValueHelperModel[] = [];
    public constructor(
        private readonly eventService: EventService,
        @Inject(OdataValueHelperInstanceToken) private readonly odataValueHelperService: OdataValueHelperInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly paceInsights: PaceInsightsService,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    public init(): Observable<void> {
        return this.odataValueHelperService.getValueHelpers().pipe(
            tap((models) => {
                this.paceInsights.trackEvent(
                    'loaded_value_helpers',
                    Object.fromEntries(models.map((m) => [m.id, `${m.code} (${m.descText})`])),
                );
                this.models = models.filter((vh) => vh.descText !== '?');
                this.isInitialized = true;
            }),
            map((): void => undefined),
        );
    }

    private checkIfServiceIsInitialized(methodName: string): void {
        if (!this.isInitialized) {
            throw new Error(`${methodName}: Trying to call ValueHelperService before initialization!`);
        }
    }

    /**
     * Based on NotificationType and code (MI, MS, MM, etc..) get the DescTxt which contains
     * the different reasons a user may choose to justify not completing a form
     * when attempting to sign a notification as completed
     *
     * @param codeLevel Represents the Notification Type: MM, MI (MS for mock mode testing only)
     * @returns ValueHelperModel[] containing the reasons for dropdown
     */
    public getReasonsMissingProtocolsByCode(codeLevel: string): ValueHelperModel[] {
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMissingProtocols).filter(
            (valueHelper) => valueHelper.codeLevel === codeLevel,
        );
    }

    /**
     * Filter value Helpers for given id
     * sort by sortField (default sort logic)
     *
     * @param id
     */
    private getValueHelpersById(id: string): ValueHelperModel[] {
        const valueHelpers = this.models.filter((model) => model.id === id);
        return ValueHelperService.sortBySortFieldNumeric(valueHelpers);
    }

    /**
     * Collect all codes belonging to an ID in a list and add it to a dictionary for easy access.
     *
     * @param id
     * @private
     */
    private readCodeOfValueHelperById(id: string): void {
        const valueHelpers = this.getValueHelpersById(id);
        this.codeListForId[id] = valueHelpers.map((valueHelper) => valueHelper.code);
    }

    /**
     * Sort value Helpers by (numeric) "sortField"
     *
     * @param valueHelpers
     */
    private static sortBySortFieldNumeric(valueHelpers: ValueHelperModel[]): ValueHelperModel[] {
        return valueHelpers.sort((valueHelper1, valueHelper2) => {
            const valueHelper1numeric = parseInt(valueHelper1.sortField, 10);
            const valueHelper2numeric = parseInt(valueHelper2.sortField, 10);
            if (isNaN(valueHelper1numeric)) {
                return 1;
            }
            if (isNaN(valueHelper2numeric)) {
                return -1;
            }
            return valueHelper1numeric - valueHelper2numeric;
        });
    }

    /**
     * Sort value Helpers by (string) "code"
     *
     * @param valueHelpers
     */
    private static sortByCode(valueHelpers: ValueHelperModel[]): ValueHelperModel[] {
        return valueHelpers.sort((a, b) => (a.code < b.code ? -1 : 1));
    }

    /**
     * Sort value Helpers by (numeric) "code"
     *
     * @param valueHelpers
     */
    private static sortByCodeNumeric(valueHelpers: ValueHelperModel[]): ValueHelperModel[] {
        return valueHelpers.sort((a, b) => (parseInt(a.code, 10) < parseInt(b.code, 10) ? -1 : 1));
    }

    /**
     * Sort value Helpers by "codeLevel"
     *
     * @param valueHelpers
     */
    private static sortByCodeLevel(valueHelpers: ValueHelperModel[]): ValueHelperModel[] {
        return valueHelpers.sort((a, b) => (a.codeLevel < b.codeLevel ? -1 : 1));
    }

    /**
     * Get the notification type string
     *
     * @param notificationType
     */
    public getNotificationTypeLabel(notificationType: ConfigConstants.NotificationTypes): string {
        this.checkIfServiceIsInitialized(`getNotificationTypeLabel`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDNotificationType);
        const type = valueHelpers.find((vh) => vh.code === notificationType);
        return type ? type.descText : '';
    }

    /**
     * Get value helper entries with ID 'EquipmentBKInfo'.
     */
    public getEquipmentBKInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentBKInfo`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDEquipmentBKInfo);
    }

    /**
     * Get value helper entries with ID 'CommercialInfoDefaultCode'.
     */
    public getCommercialInfoDefaultCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCommercialInfoDefaultCode`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDCommercialInfoDefaultCode);
    }

    /**
     * Get value helper entries with ID 'NotificationEffectCode'.
     */
    public getNotificationEffectCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getNotificationEffectCode`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDNotificationEffectCode);
    }

    /**
     * For NGMP58 Get value helper entries with ID 'EquipmentErrorCode' and CODELEVEL= blank or equal to notification's TaskGroup(APPS/CSE).
     *
     * @param codeLevel
     */
    public getEquipmentErrorCode(codeLevel: string): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentErrorCode`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDEquipmentErrorCode).filter(
            (vh) => vh.codeLevel === '' || vh.codeLevel === codeLevel,
        );
    }

    /**
     * Get value helper entries with ID 'EquipmentReason'.
     */
    public getEquipmentReason(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentReason`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDEquipmentReason);
    }

    /**
     * Get value helper entries with ID 'EquipmentStatusAfterFix'.
     */
    public getEquipmentStatusAfterFix(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentStatusAfterFix`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDEquipmentStatusAfterFix);
    }

    /**
     * Get value helper entries with ID 'EquipmentStatusInArrival'.
     */
    public getEquipmentStatusInArrival(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getEquipmentStatusInArrival`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDEquipmentStatusInArrival);
    }

    public getCentriCareStatusAfterFix(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCentriCareStatusAfterFix`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDCentriCareStatusAfterFix);
    }

    public getCentriCareStatInArrival(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCentriCareStatInArrival`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDCentriCareStatInArrival);
    }

    /**
     * Get value helper entries with ID 'ExpenseType'.
     */
    public getExpenseType(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getExpenseType`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDExpenseType);
    }

    /**
     * Get value helper entries with ID 'MaterialMK'.
     */
    public getMaterialMK(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialMK`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialMK).map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get value helper entries with ID 'TimeRecordingTaskStatus'.
     */
    public getTimeRecordingBillableCodes(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimeRecordingBillableCodes`);

        let timeRecordingCodes: ValueHelperModel[] = [];
        timeRecordingCodes.push(...this.getValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingBillable));
        if (this.configService.isCustomerDamageEnabled()) {
            timeRecordingCodes.push(...this.getValueHelpersById(ConfigConstants.valueHelperIdCustomerDamageCodes));
        }
        if (this.configService.isFocReplacementEnabled()) {
            timeRecordingCodes = timeRecordingCodes
                .filter((code) => code.code !== ConfigConstants.BILLABLE_CODE.COURTESY)
                .concat(this.getValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalLaborCodes));
            this.readCodeOfValueHelperById(ConfigConstants.valueHelperIdFocAdditionalLaborCodes);
        }
        return timeRecordingCodes;
    }

    /**
     * Get value helper entries with ID 'TimeRecordingTaskTypeInfo'.
     */
    public getTimeRecordingTaskTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimeRecordingTaskTypeInfo`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo);
    }

    /**
     * Get value helper entries with ID 'TimeRecordingTaskTypeInfo'.
     * If notification current status is ROSR/RTOBS i.e. Remote Work/Training then Task types are BREAK,WAIT and Remote Work/Training
     * If notification current status is FCC/FTOBS i.e. Remote Support then Task types are BREAK,WAIT and Remote Support
     *
     * @param status
     */
    public getRemoteWorkTimeRecordingTaskTypeInfo(status: StatusModel): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getRemoteWorkTimeRecordingTaskTypeInfo`);
        let valueHelperModel: ValueHelperModel[] = [];
        const valueHelper = this.getValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo);

        if (status.isRemoteWork()) {
            valueHelperModel = this.filterRemoteWorkTaskTypeCode(valueHelper, status);
        } else {
            valueHelperModel = this.removePhoneFixTaskTypeCode(valueHelper);
        }

        return valueHelperModel;
    }

    /**
     * This function is remove phone fix
     * We get all TaskType code from gateway
     * If user is OSR, then we have to remove all phone fix related task type code from dropdown
     * PHONE_FIX, PHONE_FIX_EXPENSES, ROSR, ROSN, FCC and FCCN is considered as phone fix
     *
     * @param valueHelper
     */
    private removePhoneFixTaskTypeCode(valueHelper: ValueHelperModel[]): ValueHelperModel[] {
        const phoneFixTypeCode: TIMERECORDING_TYPE_OF_WORK_CODES[] = [];

        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX);
        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES);
        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSR);
        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCC);
        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSN);
        phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCCN);

        return valueHelper.filter(
            (values) => !phoneFixTypeCode.includes(<TIMERECORDING_TYPE_OF_WORK_CODES>values.code),
        );
    }

    /**
     * This function is to filter only Task type code related to remote work
     * If user is ROSR i.e. Remote work/Training - [BREAK, WAIT, ROSR, ROSN] should be displayed in dropdown
     * If user is FCC i.e. Remote Support - [BREAK, WAIT, FCC, FCCN ] should be displayed in dropdown
     *
     * @param valueHelper
     * @param currentStatus
     */
    private filterRemoteWorkTaskTypeCode(
        valueHelper: ValueHelperModel[],
        currentStatus: StatusModel,
    ): ValueHelperModel[] {
        const remoteWorkTypeCode: TIMERECORDING_TYPE_OF_WORK_CODES[] = [];

        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.BREAK);
        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.WAIT);

        // when user selects ROSR and do 'sign later' then RTOBS status gets saved
        // when user selects FCC and do 'sign later' then FTOBS status gets saves as status
        if (currentStatus.isRemoteWorkOrTraining()) {
            remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSR);
            remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSN);
        } else if (currentStatus.isRemoteSupport()) {
            remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCC);
            remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCCN);
        }

        return valueHelper.filter((values) =>
            remoteWorkTypeCode.includes(<TIMERECORDING_TYPE_OF_WORK_CODES>values.code),
        );
    }

    public getPhoneFixIndicator(): boolean {
        this.checkIfServiceIsInitialized(`getPhoneFixIndicator`);

        const phoneFixIndicator = this.getPdaConfigValues().find(
            (pdaConfig) => pdaConfig.code === ConfigConstants.configKeyPhoneFixIndicatorAA,
        );
        return !!phoneFixIndicator && !!phoneFixIndicator.descText;
    }

    /**
     * Returns the means of transportation used in NGM AA
     */
    public getMeansOfTransportation(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getMeansOfTransportation`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMeansOfTransportation);
    }

    /**
     * Returns the travel destination used in NGM AA
     */
    public getTravelDestination(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTravelDestination`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDTravelDestination);
    }

    public getTimesheetGapTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimesheetGapTypeInfo`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDTimesheetGapTypeInfo);
    }

    /**
     * Get the helper entries with ID 'CreateMatOrderPriority'.
     */
    public getCreateMatOrderPriority(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getCreateMatOrderPriority`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDCreateMatOrderPriority);
    }

    /**
     * Get the helper entries with ID 'FollowUpUMRCodes'.
     */
    public getFollowUpUMRCode(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowUpUMRCode`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFollowUpUMRCode);
    }

    /**
     * Get the helper entries with ID 'Billable'.
     */
    public getBillable(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getBillable`);
        let materialBillableCodes: ValueHelperModel[] = [];
        materialBillableCodes.push(...this.getValueHelpersById(ConfigConstants.valueHelperIDBillable));
        if (this.configService.isCustomerDamageEnabled()) {
            materialBillableCodes.push(...this.getValueHelpersById(ConfigConstants.valueHelperIdCustomerDamageCodes));
        }
        if (this.configService.isFocReplacementEnabled()) {
            materialBillableCodes = materialBillableCodes
                .filter((code) => code.code !== ConfigConstants.BILLABLE_CODE.COURTESY)
                .concat(this.getValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalMaterialCodes));
            this.readCodeOfValueHelperById(ConfigConstants.valueHelperIdFocAdditionalMaterialCodes);
        }
        return materialBillableCodes.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'MaterialErrorTubeJ'.
     */
    public getMaterialErrorTubeJ(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialErrorTubeJ`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialErrorTubeJ);
        return valueHelpers.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'MaterialErrorTubeI'.
     */
    public getMaterialErrorTubeI(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialErrorTubeI`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialErrorTubeI);
        return valueHelpers.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'MaterialBug'.
     */
    public getMaterialBug(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialBug`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialBug);
        return valueHelpers.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'ReturnType'.
     */
    public getReturnType(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getReturnType`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDReturnType).map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'MaterialReturnCause'.
     */
    public getMaterialReturnCause(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialReturnCause`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialReturnCause).map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'MaterialPhiDataCode'.
     */
    public getMaterialPhiDataCode(): Array<DisplayDecorator<TextModel>> {
        this.checkIfServiceIsInitialized(`getMaterialPhiDataCode`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialPhiDataCode).map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.codeLevel, vh.code)),
        );
    }

    /**
     * Get the helper entries with ID 'QuoteType'.
     */
    public getQuoteType(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getQuoteType`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDQuoteType);
    }

    /**
     * get the helper entries with ID 'QuestionID'
     */
    public getComplaintQuestions(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getComplaintQuestions`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDComplaintQuestions);
    }

    /**
     * Get the follow-up task dispatch time for short confirmation with ID 'FollowDispatchTime'
     */
    public getFollowDispatchTime(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowDispatchTime`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFollowDispatchTime);
    }

    /**
     * Get the quality control questions for NGMAA
     */
    public getQualityControlQuestions(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getQualityControlQuestions`);
        return ValueHelperService.sortByCodeLevel(
            this.getValueHelpersById(ConfigConstants.valueHelperIDQualityControlQuestions),
        );
    }

    public getTimesheetActivityTypeInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTimesheetActivityTypeInfo`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDTimesheetActivityType);
    }

    public getPdaConfigValues(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getPdaConfigValues`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDPdaConfig);
    }

    public getOsrLimitValueHelper(): ValueHelperModel | undefined {
        this.checkIfServiceIsInitialized(`getOsrLimitValueHelper`);
        return this.getPdaConfigValues().find((pdaConfig) => pdaConfig.code === ConfigConstants.configKeyOsrLimitAA);
    }

    /**
     * Get the helper entries with ID 'AttendeeTitles'.
     */
    public getAttendeeTitles(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getAttendeeTitles`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDAttendeeTitles);
    }

    public getParticipantTitles(): FormParticipantTitles {
        const attendeeTitles = this.getAttendeeTitles();
        const participantTitles: Mutable<FormParticipantTitles> = {};

        attendeeTitles.forEach((attendeTitle) => {
            participantTitles[attendeTitle.descText] = {
                displayValue: attendeTitle.descText,
                value: attendeTitle.descText,
            };
        });

        return participantTitles;
    }
    /**
     * Get the helper entries with ID 'ModalityValues'.
     *
     * @param modality
     */
    public getModalityValue(modality: string): ValueHelperModel | undefined {
        this.checkIfServiceIsInitialized(`getModalityValue`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDModalityValues);
        return valueHelpers.find((vh) => vh.code === modality);
    }

    public getFollowupReason(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFollowupReason`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFollowupReason);
    }

    public getSRSVPNConnection(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getSRSVPNConnection`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDSRSVPNConnection);
    }

    public getAddMaterialTypes(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getAddMaterialTypes`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDAddMaterialType);
    }

    /**
     * Get the message to be displayed when user returns a material with special dangerous goods indicator
     */
    public getDangerousGoodWarning(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getDangerousGoodWarning`);
        return ValueHelperService.sortByCode(
            this.getValueHelpersById(ConfigConstants.valueHelperIDDangerousGoodWarning),
        );
    }

    public getFurtherLinks(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFurtherLinks`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFurtherLinks);
    }

    public getTravelOfficeStart(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getTravelOfficeStart`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDTravelOfficeStart).sort((a, b) =>
            a.sortField < b.sortField ? -1 : 1,
        );
    }

    public getCustomerSignatureAgreementTextClause(): string {
        this.checkIfServiceIsInitialized(`getCustomerSignatureAgreementTextClause`);
        const valueHelpers = this.getValueHelpersById(ConfigConstants.valueHelperIDTextClause);
        const textClause = valueHelpers.find((value) => typeof value !== undefined);
        return textClause ? textClause.descText : '';
    }

    public getUploadToDocboxValueHelpers(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getUploadToDocboxValueHelpers`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDUploadToDocbox);
    }

    /**
     * get the helper entries with ID 'MaterialConsumedLabel'
     */
    public getMaterialConsumptionFieldsLabels(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getMaterialConsumptionFieldsLabels`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDMaterialConsumedLabel);
    }

    public getStatesOfUSA(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getStatesOfUSA`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDStatesOfUSA);
    }

    public getFDAFormIntendedUses(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormIntendedUses`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFDAFormIntendedUses);
    }

    public getXRaySystems(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getXRaySystems`);
        return this.getValueHelpersById(ConfigConstants.valueHelperXraySystems);
    }

    public getFDAFormCertifiedComponent(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormCertifiedComponent`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFDAFormCertifiedComponent);
    }

    public getFDAFormOtherCertifiedComponent(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormOtherCertifiedComponent`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFDAFormOtherCertifiedComponent);
    }

    public getFDAFormComponentInfo(): ValueHelperModel[] {
        this.checkIfServiceIsInitialized(`getFDAFormComponentInfo`);
        return this.getValueHelpersById(ConfigConstants.valueHelperIDFDAFormComponentInfo);
    }
}
